import { useEffect, useRef } from 'react';
import io from 'socket.io-client';
import { config } from '../config/config';
import { msalInstance, apiTokenRequest } from '../services/authConfig';
import { createPushRefreshScheduler, PushRefreshScheduler } from '../utils/pushRefreshScheduler';

type Socket = ReturnType<typeof io>;

/**
 * Payload of the `dispatch_board_update` broadcast. It deliberately carries no
 * row data — it's an invalidation ping, and the client answers it with a
 * refetch. The pro/leg are for logging and future targeting.
 */
export interface DispatchBoardUpdate {
  update_type: string;
  pronumber?: number | null;
  legnum?: number | null;
  reason?: string | null;
}

interface UseDispatchBoardPushOptions {
  /** Connect the socket. False tears any existing connection down. */
  enabled: boolean;
  /**
   * Hold off refreshing — a modal is open and a data swap under it would be
   * disruptive. Pushes that land while suppressed retry rather than drop.
   */
  suppressed?: boolean;
  /** Refetch the board. Re-read on every fire, so it need not be stable. */
  onUpdate: () => void;
  debounceMs?: number;
  maxJitterMs?: number;
}

/**
 * Refreshes the dispatch board when another user changes something, without
 * waiting for the 30s poll.
 *
 * The poll stays on: bucket membership also changes from V1 CMS, EDI, the trace
 * webhooks and the ordersall -> orderscurrent sync, none of which emit. A push
 * is an optimization on top of the poll, never the only path to fresh data — so
 * every failure here (no token, socket down, worker without the Redis adapter)
 * degrades to "as fresh as it was before", not to a stale board.
 */
export function useDispatchBoardPush({
  enabled,
  suppressed = false,
  onUpdate,
  debounceMs = 3000,
  maxJitterMs = 2000,
}: UseDispatchBoardPushOptions): void {
  const onUpdateRef = useRef(onUpdate);
  onUpdateRef.current = onUpdate;

  const suppressedRef = useRef(suppressed);
  suppressedRef.current = suppressed;

  const schedulerRef = useRef<PushRefreshScheduler | null>(null);
  if (schedulerRef.current === null) {
    schedulerRef.current = createPushRefreshScheduler({
      onFire: () => {
        // A hidden tab doesn't refetch — useVisibilityAwareInterval already
        // fires the moment the user comes back, which covers this push.
        if (typeof document !== 'undefined' && document.hidden) return;
        // Mid-modal: keep the push alive and try again after the next window
        // instead of dropping the user's cue that the board moved.
        if (suppressedRef.current) {
          schedulerRef.current?.schedule();
          return;
        }
        onUpdateRef.current();
      },
      debounceMs,
      maxJitterMs,
    });
  }

  useEffect(() => {
    const realtimeEnabled = import.meta.env.VITE_REALTIME_ENABLED !== 'false';
    if (!enabled || !realtimeEnabled) return;

    let socket: Socket | null = null;
    let cancelled = false;

    const initializeSocket = async () => {
      try {
        const accounts = msalInstance.getAllAccounts();
        if (!accounts || accounts.length === 0) return;

        const response = await msalInstance.acquireTokenSilent({
          ...apiTokenRequest,
          account: accounts[0],
        });
        if (!response.accessToken || cancelled) return;

        const socketUrl = config.API_URL.replace('/api/v1', '');

        socket = io(socketUrl, {
          path: '/ws',
          auth: { token: `Bearer ${response.accessToken}` },
          // WebSocket only — polling needs sticky sessions to survive the
          // load balancer (see useRealtimeCollaboration).
          transports: ['websocket'],
          reconnection: true,
          reconnectionDelay: 2000,
          reconnectionDelayMax: 10000,
          reconnectionAttempts: 3,
          timeout: 10000,
        });

        if (cancelled) {
          socket.disconnect();
          socket = null;
          return;
        }

        socket.on('dispatch_board_update', (_data: DispatchBoardUpdate) => {
          schedulerRef.current?.schedule();
        });

        socket.on('connect_error', (error: Error) => {
          console.warn('[Socket.IO DispatchBoard] Connection error:', error.message);
        });
      } catch (error) {
        console.warn('[Socket.IO DispatchBoard] Failed to initialize socket:', error);
      }
    };

    initializeSocket();

    return () => {
      cancelled = true;
      schedulerRef.current?.cancel();
      if (socket) {
        socket.disconnect();
        socket = null;
      }
    };
  }, [enabled]);
}
